<template>
  <div v-if="localItems.length > 0" class="mb-4">
    <h5 class="mb-3">{{ title }}</h5> 

    <div
      class="invite-card"
      v-for="event in localItems"
      :key="event.id || event.eventid"
    >
      <div class="invite-header">
        <router-link
          class="host-info"
          :to="`/profile/${event.username || event.userid}`"
        >
          <img
            :src="avatarSrc(event.name, event.user_img)"
            alt="Host avatar"
          />
          <div>
            <span class="label">Host</span>
            <strong>{{ event.name }}</strong>
          </div>
        </router-link>
        <div v-if="event.feedback == 1">
        <button
          class="icon-btn"
          v-if="Number(event.feedback_status) === 0"
          @click="openFeedback(event)"
          :aria-label="`Give feedback for ${event.orgname || 'event'}`"
        >
          <i class="fas fa-comment-dots"></i>
        </button>
        <div v-else class="icon-pill" title="Feedback submitted">
          <i class="fas fa-comment-alt"></i>
        </div>
        </div>
      </div>

      <router-link class="invite-body" :to="`/received/${event.eventid}`">
        <div class="cover-wrapper">
          <div class="cover-blur" :style="coverBgStyle(event)" aria-hidden="true"></div>
          <img class="cover-image" v-no-right-click :src="getPhoto(event)" alt="Event cover" />
        </div>
        <div class="invite-details">
          <h5>{{ truncate(event.orgname) }}</h5>
          <p class="detail-line">
            <span class="emoji-icon" aria-hidden="true">📅</span>
            <span>{{ eventRangeText(event) }}</span>
          </p>
          <p class="detail-line">
            <span class="emoji-icon" aria-hidden="true">📍</span>
            {{ event.fulladdress || 'Address not available' }}
          </p>
          <p class="detail-line">
            <i class="far fa-user"></i>
            Host: {{ event.hostname }}
          </p>
        </div>
      </router-link>

      <div
        v-if="isRsvpEnabled(event) && ((event.active_status === 1 && Number(event.status) === 0) || (event.active_status === 0 && title !== 'Past Invitations' && Number(event.status) === 0))"
        class="invite-actions"
      >
        <button class="btn-pill yes" @click="openRsvpModal(event, 'new', 1)">Yes</button>
        <button class="btn-pill no" @click="openRsvpModal(event, 'new', 2)">No</button>
        <button class="btn-pill maybe" @click="openRsvpModal(event, 'new', 3)">Maybe</button>
      </div>

      <div class="invite-status" v-else-if="isRsvpEnabled(event)">
        <div class="status-text">
          <p class="mb-1" :class="statusColor(Number(event.status))">
            <i class="fas fa-check-double mr-1"></i>
            {{ responseSummary(event) }}
          </p>
        </div>
        <button
          v-if="[1,2,3].includes(Number(event.status))"
          class="btn-pill outline"
          @click="openRsvpModal(event, 'update', Number(event.status))"
        >
          Update RSVP
        </button>
      </div>
      <div
        class="connections-highlight"
        v-if="event.connections_text"
      >
        <i class="fas fa-users"></i>
        <p class="connections-text" v-html="formatConnectionsText(event.connections_text)"></p>
      </div>
    </div>

    <!-- RSVP Modal (centered) -->
    <div v-if="rsvpOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-card rsvp-modal" role="dialog" aria-modal="true" aria-labelledby="rsvpTitle">
        <div class="modal-header">
          <h5 id="rsvpTitle" class="mb-0">
            {{ modalMode === 'new' ? 'Invitation Confirmation' : 'Invitation Confirmation' }}
          </h5>
          <button class="close-btn" @click="closeModal" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body rsvp-body">
          <div class="rsvp-meta" v-if="modalEvent">
            <div class="rsvp-meta__name">{{ modalEvent.hostname || modalEvent.name || 'Host' }}</div>
            <div class="rsvp-meta__sub" v-if="modalEvent.username">@{{ modalEvent.username }}</div>
          </div>

          <!-- For update mode: status switcher -->
          <div class="rsvp-choice-row" v-if="modalMode === 'update' && modalRsvpEnabled">
            <button
              type="button"
              class="rsvp-chip yes"
              :class="{ active: modalStatus === 1 }"
              @click="selectStatus(1)"
            >
              Yes
            </button>
            <button
              type="button"
              class="rsvp-chip no"
              :class="{ active: modalStatus === 2 }"
              @click="selectStatus(2)"
            >
              No
            </button>
            <button
              type="button"
              class="rsvp-chip maybe"
              :class="{ active: modalStatus === 3 }"
              @click="selectStatus(3)"
            >
              Maybe
            </button>
          </div>

          <!-- For new mode: show selected choice -->
          <div class="mb-2" v-else-if="modalRsvpEnabled">
            <span class="badge" :class="badgeClass(modalStatus)">
              {{ statusLabel(modalStatus) }}
            </span>
          </div>

          <!-- Counts -->
          <div class="rsvp-grid" v-if="modalFamilyEnabled">
            <div class="rsvp-counter">
              <label :for="`list-adults-${modalId || 'new'}`">Adults</label>
              <div class="stepper" :class="{ disabled: modalStatus === 2 }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2 || modalAdult <= 0"
                  @click="changeCount('adult', -1)"
                >−</button>
                <div class="step-value" :aria-live="modalStatus === 2 ? 'off' : 'polite'">{{ modalAdult }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2"
                  @click="changeCount('adult', 1)"
                >+</button>
              </div>
            </div>
            <div class="rsvp-counter">
              <label :for="`list-children-${modalId || 'new'}`">Children</label>
              <div class="stepper" :class="{ disabled: modalStatus === 2 }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2 || modalChild <= 0"
                  @click="changeCount('child', -1)"
                >−</button>
                <div class="step-value" :aria-live="modalStatus === 2 ? 'off' : 'polite'">{{ modalChild }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2"
                  @click="changeCount('child', 1)"
                >+</button>
              </div>
            </div>
          </div>

          <p v-if="modalStatus === 2 && modalFamilyEnabled" class="rsvp-note">
            When choosing <strong>No</strong>, adults and children are set to 0.
          </p>

          <div class="form-group mb-0">
            <label class="small" :for="`rsvp-notes-${modalId || 'new'}`">Notes (optional)</label>
            <textarea
              class="form-control"
              rows="3"
              :id="`rsvp-notes-${modalId || 'new'}`"
              v-model.trim="modalNotes"
              placeholder="Add any notes for the host"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer rsvp-actions">
          <button class="btn btn-light rsvp-btn" @click="closeModal">Cancel</button>
          <button class="btn btn-primary rsvp-btn primary" :disabled="saving" @click="saveRsvp">
            <span v-if="!saving">Save</span>
            <span v-else class="spinner-border spinner-border-sm">Saving</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Feedback Modal -->
    <div v-if="feedbackOpen" class="modal-overlay" @click.self="closeFeedback">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="feedbackTitle">
        <div class="modal-header">
          <h5 id="feedbackTitle" class="mb-0">Feedback</h5>
          <button class="close-btn" @click="closeFeedback" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body">
          <div class="d-flex align-items-center mb-3">
            <img v-no-right-click :src="feedbackEventPreview.eventphoto || feedbackEventPreview.p1_url || placeholderImage" class="rounded mr-3" style="width:72px;height:72px;object-fit:cover" alt="event"/>
            <div>
              <div style="font-weight:700">{{ feedbackEventPreview.orgname || 'Event' }}</div>
              <div class="small text-muted">Host: {{ feedbackEventPreview.hostname || '-' }}</div>
            </div>
          </div>

          <div class="mb-3" v-if="showRatingPicker">
            <div class="small text-muted mb-2">How are you feeling about this event?</div>
            <div class="feedback-scale">
              <vue-feedback-reaction
                v-model="feedbackRating"
                :labels="feedbackLabels"
                :emoji-width="54"
                :emoji-height="54"
              /> 
            </div>
          </div>

          <div class="form-group">
            <label class="small">Add a comment</label>
            <textarea class="form-control" rows="4" v-model="feedbackMessage" placeholder="Write your feedback..."></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="closeFeedback" :disabled="savingFeedback">Cancel</button>
          <button class="btn btn-primary" @click="saveFeedback" :disabled="savingFeedback">
            <span v-if="!savingFeedback">Submit Feedback</span>
            <span v-else class="spinner-border spinner-border-sm">Submitting</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import api from '@/services/api'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import { useSessionStore } from '@/stores/session'
import { formatDisplayDate, formatEventDateRange } from '@/utils/date'
import VueFeedbackReaction from '@/components/common/FeedbackReaction.vue'



export default {
  name: 'ReceivedInvitationList',
  components: { VueFeedbackReaction },
  
  props: {
    title: String,
    items: {
      type: Array,
      default: () => []
    }
  },
  
  emits: ['rsvp-updated'],
  
  setup(props, { emit }) {
    /* ---------- Local copy to avoid mutating props directly ---------- */
    const localItems = ref(Array.isArray(props.items) ? props.items.slice() : [])
    watch(() => props.items, (n) => {
      localItems.value = Array.isArray(n) ? n.slice() : []
    }, { deep: true })

    /* ---------- Avatar helpers (dummy with initial) ---------- */
    const avatarSrc = (name, img) => {
      if (img && String(img).trim() !== '' && !String(img).includes('default_image')) return img
      const initial = (name || 'U').toString().trim().charAt(0).toUpperCase() || 'U'
      return `https://dummyimage.com/200x200/222/fff.png?text=${encodeURIComponent(initial)}`
    }

    /* ---------- Small utilities ---------- */
    function getPhoto(event) {
      return event.eventphoto && event.eventphoto !== '0'
        ? event.eventphoto
        : 'https://dummyimage.com/200x200/222/222.png'
    }
    const coverBgStyle = (event) => ({ backgroundImage: `url(${getPhoto(event)})` })
    function formatDate(dateStr) {
      if (!dateStr) return '—'
      const formatted = formatDisplayDate(dateStr)
      return formatted || dateStr
    }
    function eventRangeText(event = {}) {
      const text = formatEventDateRange({
        fromDate: event.fromdate,
        toDate: event.todate,
        fromTime: event.fromtime,
        toTime: event.totime
      })
      return text || 'Date not specified'
    }
    function truncate(str, maxLength = 60) {
      if (!str) return ''
      return str.length > maxLength ? str.substring(0, maxLength) + '…' : str
    }

    function formatConnectionsText(text = '') {
      return String(text).replace(/(\d+)/g, '<span class="connections-number">$1</span>')
    }

    // feature flags per event
    const normalizeFlag = (val) => {
      const s = String(val ?? '').trim().toLowerCase()
      if (s === '1' || s === 'yes' || s === 'true') return true
      return Number(s) === 1
    }
    const isRsvpEnabled = (event) => normalizeFlag(event?.rsvp_option)
    const isFamilyEnabled = (event) => normalizeFlag(event?.rsvp_family)

    /* --------------------- RSVP modal state --------------------- */
    const rsvpOpen   = ref(false)
    const modalMode  = ref('new') // 'new' | 'update'
    const modalId    = ref(null)  // invitation id
    const modalAdult = ref(0)
    const modalChild = ref(0)
    const modalStatus= ref(1)     // 1 yes, 2 no, 3 may
    const modalNotes = ref('')
    const modalEvent = ref(null)
    const modalRsvpEnabled = ref(true)
    const modalFamilyEnabled = ref(true)
    const saving     = ref(false)

    function statusLabel(s) {
      if (s === 1) return 'YES'
      if (s === 2) return 'NO'
      return 'MAY'
    }
    function badgeClass(s) {
      if (s === 1) return 'badge-success'
      if (s === 2) return 'badge-danger'
      return 'badge-primary'
    }

    function responseSummary(event) {
      const status = Number(event.status)
      const withCounts = isFamilyEnabled(event)
      if (status === 1) {
        return withCounts
          ? `YES • Adults: ${event.adult || 0}, Children: ${event.child || 0}`
          : 'YES'
      }
      if (status === 2) {
        return withCounts
          ? `NO • Adults: ${event.adult || 0}, Children: ${event.child || 0}`
          : 'NO'
      }
      if (status === 3) {
        return withCounts
          ? `MAYBE • Adults: ${event.adult || 0}, Children: ${event.child || 0}`
          : 'MAYBE'
      }
      return 'You have not responded'
    }

    function statusColor(status) {
      if (status === 1) return 'status-yes'
      if (status === 2) return 'status-no'
      if (status === 3) return 'status-maybe'
      return 'status-none'
    }

    function selectStatus(s) {
      modalStatus.value = s
      if (s === 2) {
        modalAdult.value = 0
        modalChild.value = 0
      }
    }

    const changeCount = (field, delta) => {
      if (!modalFamilyEnabled.value || modalStatus.value === 2) return
      const apply = (val) => Math.max(0, Number(val || 0) + delta)
      if (field === 'adult') modalAdult.value = apply(modalAdult.value)
      if (field === 'child') modalChild.value = apply(modalChild.value)
    }


    const session = useSessionStore()

    function openRsvpModal(event, mode, presetStatus) {
      if (!isRsvpEnabled(event)) return
      modalMode.value   = mode
      modalId.value     = Number(event.id || event.eventid || event.id)
      modalStatus.value = Number(presetStatus || event.status || 1)
      modalEvent.value  = event
      modalRsvpEnabled.value = isRsvpEnabled(event)
      modalFamilyEnabled.value = isFamilyEnabled(event)
      modalNotes.value = event?.response_note || event?.note || event?.notes || ''

      modalAdult.value = modalFamilyEnabled.value ? Number(event.adult || 0) : 0
      modalChild.value = modalFamilyEnabled.value ? Number(event.child || 0) : 0
      if (modalStatus.value === 2) {
        modalAdult.value = 0
        modalChild.value = 0
      }

      rsvpOpen.value = true
    }
    function closeModal() {
      if (saving.value) return
      rsvpOpen.value = false
      modalEvent.value = null
    }

    /* --------------------- Save to API + toast --------------------- */
    async function saveRsvp() {
      if (!modalId.value) return
      try {
        saving.value = true

        const fd = new FormData()
        fd.append('id', String(modalId.value))
        fd.append('status', String(modalStatus.value))
        const a = (!modalFamilyEnabled.value || modalStatus.value === 2) ? 0 : Math.max(0, Number(modalAdult.value || 0))
        const c = (!modalFamilyEnabled.value || modalStatus.value === 2) ? 0 : Math.max(0, Number(modalChild.value || 0))
        fd.append('adult', String(a))
        fd.append('child', String(c))
        fd.append('notes', modalNotes.value || '')

        const res = await api.updateRSVPReceived(fd).catch(() => null)
        const ok = res?.status === true || res?.data?.status === 'ok' || res?.data?.status === true
        if (ok) {
          rsvpOpen.value = false
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'RSVP updated',
            showConfirmButton: false,
            timer: 1800,
            timerProgressBar: true,
          })
          emit('rsvp-updated') // parent will reload lists
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Failed to update RSVP',
            showConfirmButton: false,
            timer: 1800,
            timerProgressBar: true,
          })
        }
      } finally {
        saving.value = false
      }
    }

    /* --------------------- Feedback modal state & logic --------------------- */
    const feedbackOpen = ref(false)
    const feedbackEventPreview = ref({})
    const feedbackMessage = ref('')
    const feedbackRating = ref('')
    const savingFeedback = ref(false)
    const feedbackLabels = ['Very poor', 'Poor', 'Average', 'Good', 'Excellant']
    const showRatingPicker = computed(() => Number(feedbackEventPreview.value?.rating) === 1)

    const placeholderImage = 'https://dummyimage.com/400x300/e9edf5/7c8697.png&text=Event'

    function openFeedback(event) {
      // open with the localItems reference (so we update the same object if needed)
      feedbackEventPreview.value = Object.assign({}, event)
      feedbackMessage.value = ''
      const existingRating = event?.feedback_rating || event?.rating
      feedbackRating.value = existingRating ? String(existingRating) : ''
      feedbackOpen.value = true
    }
    function closeFeedback() {
      if (savingFeedback.value) return
      feedbackOpen.value = false
      feedbackRating.value = ''
    }

    async function saveFeedback() {
      if (!feedbackEventPreview.value || !feedbackEventPreview.value.eventid) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Event not selected',
          showConfirmButton: false,
          timer: 2200
        })
        return
      }
      if (showRatingPicker.value && !feedbackRating.value) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'warning',
          title: 'Pick a rating (1-5)',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }
      const trimmedMsg = String(feedbackMessage.value || '').trim()
      if (!trimmedMsg) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'warning',
          title: 'Add feedback before saving',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }
      try {
        savingFeedback.value = true
        const fd = new FormData()
        fd.append('user_id', String(session.userId || ''))
        fd.append('event_id', String(feedbackEventPreview.value.eventid || feedbackEventPreview.value.event_id))
        fd.append('message', trimmedMsg)
        fd.append('rating', String(feedbackRating.value))

        const res = await api.saveFeedback(fd).catch(() => null)
        const ok = res?.status === true || res?.data?.status === 'ok' || res?.data?.status === true
        if (ok) {
          // update local copy only (no prop mutation)
          const eid = feedbackEventPreview.value.eventid
          const idx = localItems.value.findIndex(it => String(it.eventid) === String(eid) || String(it.id) === String(feedbackEventPreview.value.id))
          if (idx > -1) {
            // update the local copy to reflect feedback submitted
            localItems.value[idx] = Object.assign({}, localItems.value[idx], {
              feedback_status: 1,
              feedback_rating: feedbackRating.value
            })
          }
          feedbackOpen.value = false
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Feedback submitted',
            showConfirmButton: false,
            timer: 1800,
            timerProgressBar: true,
          })
          emit('rsvp-updated') // parent will reload lists
        } else {
          Swal.fire({ icon: 'error', title: 'Failed', text: res?.data?.msg || 'Failed to submit feedback' })
        }
      } catch (err) {
        console.error('Feedback save error', err)
        Swal.fire({ icon: 'error', title: 'Error', text: 'Unable to submit feedback' })
      } finally {
        savingFeedback.value = false
      }
    }

    return {
      // local items & utilities
      localItems,
      avatarSrc,
      getPhoto,
      formatDate,
      truncate,
      eventRangeText,
      coverBgStyle,
      responseSummary,
      statusColor,
      formatConnectionsText,
      isRsvpEnabled,
      isFamilyEnabled,

      // RSVP
      statusLabel,
      badgeClass,
      selectStatus,
      changeCount,
      openRsvpModal,
      closeModal,
      saveRsvp,

      modalRsvpEnabled,
      modalFamilyEnabled,

      rsvpOpen,
      modalMode,
      modalId,
      modalAdult,
      modalChild,
      modalStatus,
      modalEvent,
      modalNotes,
      saving,

      // Feedback
      feedbackOpen,
      feedbackEventPreview,
      feedbackMessage,
      feedbackRating,
      savingFeedback,
      feedbackLabels,
      placeholderImage,
      showRatingPicker,
      openFeedback,
      closeFeedback,
      saveFeedback
    }
  }
}
</script>

<style scoped>
.invite-card {
  --border-width: 3px;
  --fill: 0deg;
  position: relative;
  border-radius: 20px;
  padding: 1.35rem;
  background: linear-gradient(135deg, #fff7ef 0%, #fff3e6 48%, #ffe9d7 100%);
  border: var(--border-width) solid transparent;
  box-shadow: 0 22px 45px rgba(249, 115, 22, 0.12);
  margin-bottom: 1.3rem;
  transition: transform .18s ease, box-shadow .18s ease;
  overflow: visible;
  isolation: isolate;
  background-clip: padding-box;
}
.invite-card > * {
  position: relative;
  z-index: 1;
}
.invite-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 32px 60px rgba(15,23,42,0.14);
}
.invite-card::before {
  content: '';
  position: absolute;
  inset: calc(var(--border-width) * -1);
  z-index: 0;
  border-radius: inherit;
  background: conic-gradient(from -90deg, #ff7a1a 0deg, #ff9a3c var(--fill), transparent var(--fill), transparent 360deg);
  padding: var(--border-width);
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  opacity: 0;
  transition: opacity .25s ease, filter .25s ease;
  filter: drop-shadow(0 8px 18px rgba(249, 115, 22, 0.28));
}
.invite-card:hover::before {
  opacity: 1;
  animation: fillBorder .9s ease;
  filter: drop-shadow(0 10px 22px rgba(249, 115, 22, 0.34));
}

@property --fill {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@keyframes fillBorder {
  from { --fill: 0deg; }
  to { --fill: 360deg; }
}

.invite-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.host-info {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #0f172a;
}
.host-info img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 6px 16px rgba(15,23,42,0.15);
}
.host-info .label {
  display: block;
  font-size: .7rem;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: .05em;
}
.host-info strong {
  font-size: .95rem;
  color: #111827;
}

.icon-btn,
.icon-pill {
  border: none;
  background: #fff1e6;
  color: #f97316;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
}
.icon-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 16px rgba(249, 115, 22, 0.25);
}
.icon-pill {
  opacity: .7;
  cursor: default;
}

.invite-body {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 18px;
  align-items: stretch;
  margin-bottom: 16px;
  text-decoration: none;
  color: inherit;
}
.cover-wrapper {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  height: 100px;
  width:100px;
  background: #0f172a;
}
.cover-blur {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(26px) saturate(1.15);
  transform: scale(1.08);
  opacity: 0;
  transition: opacity .2s ease;
}
.cover-wrapper::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(15,23,42,.65), rgba(15,23,42,.25));
  opacity: 0;
  transition: opacity .2s ease;
  z-index: 0;
}
.cover-wrapper .cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
  z-index: 1;
}
.invite-details h5 {
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}
.detail-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  color: #475569;
  font-size: .95rem;
}
.detail-line .emoji-icon{
  color: #f97316;
  line-height:1;
  display:inline-block;
}

.invite-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.btn-pill {
  flex: 1;
  min-width: 90px;
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .02em;
  color: #fff;
  transition: transform .12s ease, box-shadow .12s ease;
}
.btn-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 20px rgba(15,23,42,0.15);
}
.btn-pill.yes { background: #FB7210; }
.btn-pill.no { background: #e5e7eb; color: #1f2937; }
.btn-pill.maybe { background: #2563eb; }
.btn-pill.outline {
  background: transparent;
  border: 1px solid rgba(15,23,42,0.2);
  color: #0f172a;
}

.invite-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.status-text p {
  margin: 0;
  font-weight: 600;
}
.status-yes { color: #FB7210; }
.status-no { color: #6b7280; }
.status-maybe { color: #2563eb; }
.status-none { color: #475569; }

.connections-highlight {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(249, 115, 22, 0.08);
  border-radius: 16px;
  padding: 0.8rem 1rem;
  margin-top: 0.75rem;
  border: 1px solid rgba(249, 115, 22, 0.15);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.4);
  color: #7a2d00;
}
.connections-highlight i {
  font-size: 1.1rem;
  color: #f97316;
}
.connections-text {
  margin: 0;
  font-weight: 600;
  color: #7a2d00;
}
.connections-number {
  font-weight: 800;
  color: #c2410c;
}

@keyframes rotateBorder {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@supports not (background: conic-gradient(red, blue)) {
  .invite-card::before {
    background-image: linear-gradient(90deg, #ff7a1a, #ff9a3c, #ffd166, #ff7a1a);
    animation: none;
  }
}

@media (max-width: 992px) {
  .invite-body {
    grid-template-columns: 1fr;
  }
  .cover-wrapper {
    height: 200px;
  }
  .cover-blur,
  .cover-wrapper::after {
    opacity: 1;
  }
  .cover-wrapper .cover-image {
    object-fit: contain;
    padding: 12px;
  }
}
@media (max-width: 768px) {
  .invite-card {
    padding: 1rem;
  }
}
@media (max-width: 576px) {
  .invite-card {
    padding: .9rem;
  }
  .cover-wrapper {
    height: 170px;
  }
  .cover-wrapper .cover-image {
    padding: 10px;
  }
  .invite-status {
    flex-direction: column;
    align-items: flex-start;
  }
  .btn-pill {
    width: 100%;
  }
}

/* ========== MODAL BASE ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}
.modal-card {
  width: min(540px, 96vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.modal-header, .modal-footer {
  padding: .85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
}
.modal-footer {
  border-top: 1px solid #f1f3f5;
  border-bottom: 0;
}
.modal-body {
  padding: 1rem;
}
.close-btn {
  background: transparent;
  border: 0;
  font-size: 1.3rem;
  color: #6c757d;
  cursor: pointer;
}
.close-btn:hover { color: #000; }

/* RSVP layout */
.rsvp-modal{
  width: min(560px, 96vw);
  border-radius: 18px;
}
.rsvp-body{
  display:flex;
  flex-direction:column;
  gap:1rem;
  padding:1.1rem;
}
.rsvp-meta{
  display:flex;
  flex-direction:column;
  gap:.2rem;
  align-items:center;
  text-align:center;
}
.rsvp-meta__name{
  font-weight:700;
  color:#0f172a;
}
.rsvp-meta__sub{
  font-size:.9rem;
  color:#6b7280;
}
.rsvp-choice-row{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap:.75rem;
}
.rsvp-chip{
  border:1px solid #e5e7eb;
  background:#ffffff;
  color:#111827;
  border-radius:18px;
  padding:.9rem .8rem;
  font-weight:700;
  text-transform:capitalize;
  width:100%;
  min-width:0;
  transition:all .15s ease;
  box-shadow:0 10px 26px rgba(15,23,42,.06);
}
.rsvp-chip.active{
  color:#0f172a;
  border-color:transparent;
  box-shadow:0 14px 26px rgba(251,114,16,.25);
}
.rsvp-chip.yes.active{ background:#FB7210; color:#fff; }
.rsvp-chip.no.active{ background:#e5e7eb; color:#374151; }
.rsvp-chip.maybe.active{ background:#e7eef9; color:#1f2a44; }
.rsvp-grid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap:.75rem;
}
.rsvp-counter{
  background:#f7f9fb;
  border:1px solid #e2e8f0;
  border-radius:16px;
  padding:.9rem;
}
.rsvp-counter label{
  display:block;
  margin-bottom:.35rem;
  font-weight:700;
  color:#0f172a;
}
.rsvp-counter .form-control{
  border-radius:10px;
}
.stepper{
  display:grid;
  grid-template-columns: repeat(3, 1fr);
  border:1px solid #d8e2ec;
  border-radius:12px;
  overflow:hidden;
  background:#f4f8fb;
}
.stepper.disabled{
  opacity:.65;
}
.step-btn{
  border:0;
  background:transparent;
  color:#1f2937;
  font-weight:700;
  padding:.55rem 0;
  transition:background .12s ease;
}
.step-btn:first-child{
  border-right:1px solid #e5e7eb;
}
.step-btn:last-child{
  border-left:1px solid #e5e7eb;
}
.step-btn:active{
  background:#e6edf5;
}
.step-value{
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:700;
  color:#111827;
  padding:.55rem 0;
}
.rsvp-note{
  font-size:.9rem;
  color:#6b7280;
  margin:0;
}
.rsvp-actions{
  display:flex;
  gap:.65rem;
  flex-wrap:wrap;
  justify-content:flex-end;
}
.rsvp-btn{
  min-width:120px;
  border-radius:12px;
  font-weight:700;
}
.rsvp-btn.primary{
  box-shadow:0 12px 24px rgba(37,99,235,.25);
}

/* ========== BADGES ========== */
.badge {
  display:inline-block;
  padding:.35rem .55rem;
  border-radius:6px;
  font-weight:600;
  font-size:.85rem;
}
.badge-success { background:#d1e7dd; color:#0f5132; border:1px solid #badbcc; }
.badge-danger  { background:#f8d7da; color:#842029; border:1px solid #f5c2c7; }
.badge-primary { background:#cfe2ff; color:#084298; border:1px solid #b6d4fe; }

/* ========== Emoji selector (feedback modal) ========== */
.feedback-scale {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.feedback-scale .vue-feedback-reaction {
  margin: 0;
}
.selected-rating {
  font-weight: 600;
  color: #0f172a;
}

/* ========== RESPONSIVE DESIGN ========== */
@media (max-width: 640px) {
  .modal-overlay{
    align-items: flex-end;
  }
  .modal-card{
    width:100%;
    border-radius:18px 18px 0 0;
  }
  .modal-body{
    max-height:65vh;
  }
}
</style>
